import { injectable } from 'inversify';

import { ITeamLeagueRepository } from '@/domain/repositories/team-league.repository.interface';
import { MyTeamLeagues, Result } from '@/entities';
import { ActivityDivisionInfoData, DivisionInfoData } from '@/entities/DivisionInfoData';
import { SearchParams } from '@/entities/SearchParams';
import { SeasonListItem } from '@/entities/SeasonList';
import { myTeamLeaguesDtoToMyTeamLeagues } from '@/infrastructure/mapper/my-team-leagues.mapper';
import { listActiveTeams } from '@/infrastructure/network/data/active-teams';
import { listMyTeamLeagues } from '@/infrastructure/network/data/my-team-leagues';
import { listPlayerActivity } from '@/infrastructure/network/data/player-activity';
import { listSeasonDivisons, listSeasons } from '@/infrastructure/network/data/seasons';
import { SeasonDivisionItem } from '@/infrastructure/network/dto/DivisionDto';

@injectable()
export class TeamLeagueRepository implements ITeamLeagueRepository {
	async getActiveTeams(organizationUuid: string, teamLeagueUuid: string, seasonUuid: string): Promise<Result<DivisionInfoData[]>> {
		const result = await listActiveTeams({
			organizationUuid,
			teamLeagueUuid,
			seasonUuid
		});
		if (result.statusCode === 200) {
			return { data: result.data, statusCode: result.statusCode };
		} else {
			return {
				statusCode: result.statusCode,
				correlationId: result.correlationId,
				error: result.error
			};
		}
	}

	async getSeasonsList(organizationUuid: string, teamLeagueUuid: string): Promise<Result<SeasonListItem[]>> {
		const result = await listSeasons({
			organizationUuid,
			teamLeagueUuid
		});
		if (result.statusCode === 200) {
			return { data: result.data.teamLeagueSeasons, statusCode: result.statusCode };
		} else {
			return {
				statusCode: result.statusCode,
				correlationId: result.correlationId,
				error: result.error
			};
		}
	}

	async getSeasonsDivisionsList(organizationUuid: string, teamLeagueUuid: string, seasonUuid: string): Promise<Result<SeasonDivisionItem[]>> {
		const result = await listSeasonDivisons({
			organizationUuid,
			teamLeagueUuid,
			seasonUuid
		});
		if (result.statusCode === 200) {
			return { data: result.data.teamLeaguesDivisions, statusCode: result.statusCode };
		} else {
			return {
				statusCode: result.statusCode,
				correlationId: result.correlationId,
				error: result.error
			};
		}
	}

	async getPlayerActivity(organizationUuid: string, teamLeagueUuid: string, seasonUuid: string): Promise<Result<ActivityDivisionInfoData[]>> {
		const result = await listPlayerActivity({
			organizationUuid,
			teamLeagueUuid,
			seasonUuid
		});
		if (result.statusCode === 200) {
			return { data: result.data, statusCode: result.statusCode };
		} else {
			return {
				statusCode: result.statusCode,
				correlationId: result.correlationId,
				error: result.error
			};
		}
	}

	async getMyTeamLeagues(searchParams: SearchParams): Promise<Result<MyTeamLeagues>> {
		const result = await listMyTeamLeagues({ searchParams });
		if (result.statusCode === 200) {
			return { data: myTeamLeaguesDtoToMyTeamLeagues(result.data), statusCode: result.statusCode };
		} else {
			return { statusCode: result.statusCode, correlationId: result.correlationId, error: result.error };
		}
	}
}
